import { useEffect, useRef, useState } from 'react';
import { Box, Button, Drawer, IconButton, MenuItem, TextField, Theme, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { FieldConfig, TabKey, masterConfig } from '../masterConfig';
import {
  useCheckDuplicateMutation,
  useGetBrandsQuery,
  useSaveMasterMutation,
  useUpdateMasterMutation,
} from '../../../api/master/MasterApi';
import { MasterRow } from '../../../types/master';
import { useSnackbar } from '../../../utils/snackbar';
import { digitsOnly, lowerEmail } from '../../../utils/clean';
import { GST_STATES, stateNameFor } from '../../../utils/states';

/*
 * The drawer below is lifted to zIndex.modal + 1 so it clears a dialog it was opened
 * from. A Select's list is a popover of its own at zIndex.modal (1300), which then
 * landed BEHIND the drawer: on the customer form the State list opened underneath the
 * panel, so no state could be picked and the form looked frozen.
 *
 * Anything that pops out of this drawer has to clear the drawer as well.
 */
const menuAboveDrawer = {
  MenuProps: { sx: { zIndex: (theme: Theme) => theme.zIndex.modal + 2 } },
};

interface PropsData {
  tab: TabKey;
  editData: MasterRow | null;
  /** Carries the saved row on a create, so an inline "+" can select it at once. */
  closeDrawer: (saved?: MasterRow) => void;
}

/**
 * The right-hand drawer used for every simple master, and for the inline "+" buttons
 * on the product, purchase and billing screens.
 *
 * <p>Fields come from masterConfig, so adding a master is a config entry rather than
 * another form.
 */
const MasterDrawer = ({ tab, editData, closeDrawer }: PropsData) => {
  const config = masterConfig[tab];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formValues, setFormValues] = useState<MasterRow>(editData ? { ...editData } : {});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [duplicate, setDuplicate] = useState(false);

  const { openSnackbar } = useSnackbar();
  const [saveMaster, { isLoading: saving }] = useSaveMasterMutation();
  const [updateMaster, { isLoading: updating }] = useUpdateMasterMutation();
  const [checkDuplicate] = useCheckDuplicateMutation();
  const { data: brands = [] } = useGetBrandsQuery();

  const editing = !!editData?.id;

  useEffect(() => {
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => {
      clearTimeout(focusTimer);
      // Closing mid-type must not leave the debounced check running.
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
    };
  }, []);

  const setValue = (field: FieldConfig, value: unknown) => {
    // Cleaned on the way in rather than checked on the way out: a box that will
    // not take a letter explains itself, an error message afterwards does not.
    const cleaned =
      field.type === 'phone'
        ? digitsOnly(String(value))
        : field.type === 'email'
          ? lowerEmail(String(value))
          : value;
    const next = { ...formValues, [field.key]: cleaned };
    setFormValues(next);
    setErrors((previous) => ({ ...previous, [field.key]: false }));
    if (field.partOfDuplicateKey) runDuplicateCheck(next);
  };

  const runDuplicateCheck = (values: MasterRow) => {
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(async () => {
      const body: MasterRow = {};
      config.fields
        .filter((field) => field.partOfDuplicateKey)
        .forEach((field) => {
          body[field.key] = values[field.key];
        });
      const name = String(values[config.nameKey] ?? '').trim();
      if (!name) {
        setDuplicate(false);
        return;
      }
      try {
        const response = await checkDuplicate({ path: config.path, body }).unwrap();
        // Editing its own row is not a clash.
        setDuplicate(response.duplicate && !editing);
      } catch {
        setDuplicate(false);
      }
    }, 500);
  };

  const handleSubmit = async () => {
    const nextErrors: Record<string, boolean> = {};
    config.fields
      .filter((field) => field.required)
      .forEach((field) => {
        const value = formValues[field.key];
        nextErrors[field.key] = value === undefined || value === null || String(value).trim() === '';
      });
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean) || duplicate) return;

    const payload: MasterRow = editing ? { ...editData } : {};
    config.fields.forEach((field) => {
      const raw = formValues[field.key];
      if (field.type === 'number') {
        payload[field.key] = raw === '' || raw === undefined || raw === null ? null : Number(raw);
      } else if (field.type === 'select') {
        payload[field.key] = raw ? Number(raw) : null;
      } else if (field.type === 'state') {
        // A GST state code is text: "07" is Delhi, and Number() would make it 7.
        payload[field.key] = raw ? String(raw) : null;
        if (field.nameKey) {
          payload[field.nameKey] = raw ? stateNameFor(String(raw)) : null;
        }
      } else {
        payload[field.key] = raw === undefined || raw === null ? null : String(raw).trim();
      }
    });

    try {
      if (editing) {
        await updateMaster({ path: config.path, body: { ...payload, id: editData?.id } }).unwrap();
        openSnackbar(`${config.title} updated`, 'success');
        closeDrawer();
        return;
      }
      const saved = await saveMaster({ path: config.path, body: payload }).unwrap();
      openSnackbar(`${config.title} saved`, 'success');
      closeDrawer(saved);
    } catch (error: any) {
      openSnackbar(error?.data?.message || `Could not save the ${config.label.toLowerCase()}`, 'error');
    }
  };

  const renderField = (field: FieldConfig, index: number) => {
    if (field.type === 'state') {
      return (
        <TextField
          key={field.key}
          select
          label={field.label}
          fullWidth
          margin="normal"
          required={field.required}
          value={formValues[field.key] ?? ''}
          helperText="Decides whether their bills are taxed CGST + SGST or IGST"
          SelectProps={menuAboveDrawer}
          onChange={(event) => setValue(field, String(event.target.value))}
        >
          <MenuItem value="">Not set - the shop's own state is used</MenuItem>
          {GST_STATES.map((state) => (
            <MenuItem key={state.code} value={state.code}>
              {state.code} &nbsp;{state.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === 'select') {
      return (
        <TextField
          key={field.key}
          select
          label={field.label}
          fullWidth
          margin="normal"
          required={field.required}
          value={formValues[field.key] ?? ''}
          error={errors[field.key]}
          helperText={errors[field.key] ? `${field.label} is required` : ''}
          SelectProps={menuAboveDrawer}
          onChange={(event) => setValue(field, String(event.target.value))}
        >
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              {brand.brandName}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    const isDuplicateField = field.partOfDuplicateKey && duplicate && field.key === config.nameKey;
    return (
      <TextField
        key={field.key}
        label={field.label}
        type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
        inputProps={field.type === 'phone' ? { inputMode: 'numeric', maxLength: 15 } : undefined}
        fullWidth
        margin="normal"
        required={field.required}
        inputRef={index === 0 ? inputRef : undefined}
        value={formValues[field.key] ?? ''}
        error={errors[field.key] || isDuplicateField}
        helperText={
          errors[field.key] ? `${field.label} is required` : isDuplicateField ? `${field.label} already exists` : ''
        }
        onChange={(event) => setValue(field, event.target.value)}
      />
    );
  };

  return (
    /*
     * Lifted above dialogs on purpose.
     *
     * A drawer sits at zIndex.drawer (1200) and a dialog at zIndex.modal (1300), so
     * opening this from inside one - New Product during a purchase, then "New brand" -
     * put it behind the dialog. It was open, and underneath, so the button looked dead.
     * From the Masters page, with no dialog in the way, the same drawer was fine, which
     * is why it seemed to work only sometimes.
     */
    <Drawer open onClose={() => closeDrawer()} anchor="right" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" padding={2} position="relative">
        <Typography variant="h6">{editing ? `Edit ${config.title}` : `New ${config.title}`}</Typography>
        <IconButton
          aria-label="close"
          onClick={() => closeDrawer()}
          sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box padding={2} width={340} sx={{ overflowY: 'auto' }}>
        {config.fields.map(renderField)}
      </Box>
      <Box display="flex" justifyContent="end" alignItems="center" padding={2} gap={1}>
        <Button variant="outlined" color="inherit" onClick={() => closeDrawer()}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={saving || updating} disabled={duplicate} onClick={handleSubmit}>
          {editing ? 'Update' : 'Save'}
        </LoadingButton>
      </Box>
    </Drawer>
  );
};

export default MasterDrawer;
